// Data Quality — every rule we check, transparently scored.
// Tells the truth about what to trust.
import { useInfoModal, PageChrome, InfoModal } from './Chrome.jsx';
import { dqRules, dqScore, SUB_PAGES } from './helpers.js';
import { DqGauge, StatusPill } from './Widgets.jsx';

const TONE_FROM_STATUS = { pass: 'green', warn: 'orange', fail: 'red' };

export function PageDataQuality({ stats, nav }) {
  const info = useInfoModal();
  const helpEntry = SUB_PAGES.find((p) => p.id === 'data_quality');
  const rules = dqRules(stats);
  const score = dqScore(rules);
  const grouped = groupByDimension(rules);
  const failing = rules.filter((r) => r.status === 'fail');
  const warning = rules.filter((r) => r.status === 'warn');

  let detail;
  if (score.score >= 90) detail = 'All checks passed. Numbers on every page can be trusted.';
  else if (failing.length > 0) detail = `${failing.length} rule${failing.length === 1 ? '' : 's'} failing — treat numbers below with caution.`;
  else if (warning.length > 0) detail = `${warning.length} rule${warning.length === 1 ? '' : 's'} in warning — investigate but data is mostly usable.`;
  else detail = '';

  return (
    <>
      <PageChrome
        title="Data Quality"
        subtitle="Truth-in-data: which rules pass, which break, and what to do about it."
        onHome={() => nav.goSubPage('overview')}
        onBack={nav.goBack} canBack={nav.canBack}
        onInfo={info.onOpen}
      />

      <DqGauge score={score.score} label={score.label} tone={score.tone} detail={detail} />

      {Object.entries(grouped).map(([dim, list]) => (
        <div className="db-card" key={dim}>
          <div className="db-card-title">{dim}</div>
          <div className="db-card-sub">{dimensionDescription(dim)}</div>
          <div className="db-dq-grid">
            {list.map((r) => {
              const tone = TONE_FROM_STATUS[r.status] || 'gray';
              return (
                <div className={`db-dq-rule tone-${tone}`} key={r.id}>
                  <StatusPill tone={tone}>{r.status}</StatusPill>
                  <div>
                    <div className="db-dq-label">{r.label}</div>
                    <div className="db-dq-detail">{r.detail}</div>
                  </div>
                  <div className="db-dq-evidence">{r.evidence}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="db-card">
        <div className="db-card-title">How the score is calculated</div>
        <ul style={{ paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
          <li>Each rule is graded <strong>pass (100)</strong>, <strong>warn (60)</strong>, or <strong>fail (0)</strong>.</li>
          <li>The overall score is the simple average across all rules.</li>
          <li>Score bands: <strong>Healthy</strong> (≥90), <strong>Watch</strong> (70–89), <strong>Degraded</strong> (40–69), <strong>Critical</strong> (&lt;40).</li>
          <li>Timeliness rules use a 30h threshold — daily pipelines should run successfully every day.</li>
          <li>Mapping coverage threshold is 15% gap rate. Above that flags as warn; above 25% flags as fail.</li>
        </ul>
      </div>

      <InfoModal open={info.open} onClose={info.onClose} title="About Data Quality" body={helpEntry?.help} />
    </>
  );
}

function groupByDimension(rules) {
  const out = {};
  for (const r of rules) {
    if (!out[r.dimension]) out[r.dimension] = [];
    out[r.dimension].push(r);
  }
  return out;
}

function dimensionDescription(dim) {
  if (dim === 'Timeliness')   return 'Did each upstream pipeline run successfully and recently?';
  if (dim === 'Correctness')  return 'Are the records mapped, valid, and free of failures?';
  if (dim === 'Completeness') return 'Is the expected data present and are all alerts cleared?';
  return '';
}
